import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Typography, withStyles } from '@material-ui/core';
import AlertDialog from '../../shared/alertDialog';
import AttributesList from '../../shared/attributesList';
import NotificationCard from '../../shared/notificationCard';
import SelectField from '../../shared/selectField';
import CustomDateRange from '../../shared/customDateRange';
import CompetenceMapChart from '../../shared/competenceMapChart';
import MatchedUsersList from '../../shared/matchedUsersList';
import PresetDropdown from '../../shared/presetDropdown';
import { PRESETS } from '../../../constants/presets';
import { PARAMS } from '../../../constants/pages';
import { INITIAL_SURVEY_TYPE_FILTER_VALUE } from '../../../constants/survey';
import { COMPETENCE_MAP_ATTRIBUTE_CONFIG } from '../../../constants/attributes';
import { sticky } from '../../../constants/helperCssRules';
import { showSuccessMessage } from '../../../utility/uiUtils';
import {
  getActivePresetId,
  getActivePresetName,
  getFilteredPresets,
  getPlaceholderName,
  handleHasMaxPresets,
  handlePresetName,
  getSelectedSurveyType,
} from '../../../utility/presets';
import { prepareSurveyTypeFilterOptions } from '../../../utility/reportUtils';
import { isUserProfileAccessibleCheck } from '../../../utility/permissions';
import {
  isArrayEmpty,
  asyncDebounce,
  isItemInList,
  areArraysContentsEqual,
  getItemById,
  getObjectToNumberArray,
  isValueInList,
} from '../../../utility/helpers';
import { getLast12Months } from '../../shared/customDateRange/config';
import {
  NOTIFICATION_DELAY,
  NOTIFICATION_CLEAR_DELAY,
  MAX_SELECTED_SCORES,
  prepareInitiallySelectedAttributes,
  prepareChartData,
  updateSelectedScores,
  handlePeriod,
  prepareMatchedUsers,
} from './config';

const styles = ({ palette: { primary }, spacing }) => ({
  description: {
    padding: spacing(8, 0, 6, 0),
    ...sticky(primary.white, 105),
  },
  attributesWrapper: {
    position: 'relative',
    marginBottom: spacing(6),
  },
  attributesLabel: {
    marginBottom: spacing(2),
  },
  notification: {
    color: primary.red1,
    position: 'absolute',
    left: 0,
    bottom: -16,
  },
  filter: {
    marginBottom: spacing(6),
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 350px))',
    gridColumnGap: 16,
  },
  chart: {
    marginTop: spacing(6),
  },
  scoresTitle: {
    margin: spacing(6, 0),
  },
});

const COMPETENCE_MAP_PRESET_ID = PRESETS.COMPETENCE_MAP;

class CompetenceMapPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      isScoreLoading: false,
      selectedAttributes: [],
      selectedScores: [],
      period: getLast12Months(),
      hasNotification: false,
      notificationMessage: '',
      alreadySavedMessage: '',
      isDeletePresetDialogOpened: false,
      selectedPreset: undefined,
      defaultPlaceholder: false,
      presetPlaceholderName: '',
      selectedSurveyType: INITIAL_SURVEY_TYPE_FILTER_VALUE,
    };
    this.handleShowMinMaxNotification = asyncDebounce(
      this.handleShowMinMaxNotification,
      NOTIFICATION_DELAY,
      true
    );
  }

  componentDidMount() {
    const { presets, translations } = this.props;
    const competenceMapPagePresets = presets[COMPETENCE_MAP_PRESET_ID];
    const existsPresetsArray =
      competenceMapPagePresets && !isArrayEmpty(competenceMapPagePresets);
    const initialPreset = existsPresetsArray
      ? getItemById(
          competenceMapPagePresets,
          presets.active[COMPETENCE_MAP_PRESET_ID]
        )
      : undefined;

    if (initialPreset) {
      const surveyTypeOptions = prepareSurveyTypeFilterOptions(
        translations.surveyTypeFilterOptions
      );

      this.setState(
        {
          selectedSurveyType:
            getSelectedSurveyType(
              surveyTypeOptions,
              initialPreset?.data?.survey_type
            ) || INITIAL_SURVEY_TYPE_FILTER_VALUE,
          selectedPreset: initialPreset,
        },
        () => this.onGetMeasuredAttributes(initialPreset)
      );
    } else {
      this.onGetMeasuredAttributes();
    }
  }

  componentWillUnmount() {
    const {
      clearMeasuredAttributes,
      clearAttributesScoreDistribution,
      clearAttributesUsersScores,
    } = this.props;

    clearMeasuredAttributes();
    clearAttributesScoreDistribution();
    clearAttributesUsersScores();
  }

  canAccessUserProfileCheck = userId => {
    const { auth, accessControl } = this.props;

    return isUserProfileAccessibleCheck(accessControl, auth.id, userId);
  };

  checkValueInPresets = (scores, period, selectableAttributes) => {
    const { presets, editPresetActivity, translations } = this.props;
    const { selectedSurveyType } = this.state;
    const competenceMapPresets = presets[COMPETENCE_MAP_PRESET_ID];

    if (competenceMapPresets && !isArrayEmpty(competenceMapPresets)) {
      const activePreset = competenceMapPresets.find(preset => {
        const presetData = preset?.data;
        const tempSelectedAttributes = getObjectToNumberArray(
          presetData.attributes
        );
        const { end, start } = presetData.period;
        const presetSurveyType =
          presetData.survey_type ?? INITIAL_SURVEY_TYPE_FILTER_VALUE.value;

        return (
          period.end === end &&
          period.start === start &&
          areArraysContentsEqual(
            tempSelectedAttributes,
            selectableAttributes
          ) &&
          areArraysContentsEqual(presetData.scores, scores) &&
          areArraysContentsEqual(presetSurveyType, selectedSurveyType.value)
        );
      });
      if (activePreset) {
        editPresetActivity(activePreset.id, COMPETENCE_MAP_PRESET_ID, true);
        this.setState({
          selectedPreset: activePreset,
          alreadySavedMessage: translations.presets.used,
        });
      } else {
        this.handleResetPreset();
      }
    }
  };

  handleResetPreset = () => {
    const { editPresetActivity } = this.props;

    editPresetActivity(-1, COMPETENCE_MAP_PRESET_ID, false);
    this.setState({
      selectedPreset: undefined,
      alreadySavedMessage: '',
    });
  };

  getFilteredAttributes = (results, initialPreset) => {
    const { selectedAttributes } = this.state;
    const tempAttributes = initialPreset
      ? initialPreset?.data?.attributes
      : selectedAttributes;
    const tempSelectedAttributes = getObjectToNumberArray(tempAttributes);

    return results.filter(attribute =>
      isValueInList(tempSelectedAttributes, attribute.id)
    );
  };

  onGetMeasuredAttributes = (initialPreset = null) => {
    const {
      getMeasuredAttributes,
      clearAttributesScoreDistribution,
      scoreDistribution,
      clearAttributesUsersScores,
    } = this.props;
    const { period, selectedScores, selectedSurveyType } = this.state;
    const tempPeriod = handlePeriod(initialPreset, period);
    const params = {
      [PARAMS.SURVEY_TYPE]:
        initialPreset?.data?.survey_type ?? selectedSurveyType.value,
      [PARAMS.USED_AFTER]: tempPeriod.start,
      [PARAMS.USED_BEFORE]: tempPeriod.end,
    };

    return getMeasuredAttributes(params).then(results => {
      const selectedAttributes = initialPreset
        ? this.getFilteredAttributes(results, initialPreset)
        : prepareInitiallySelectedAttributes(results);
      const scores = initialPreset
        ? initialPreset?.data?.scores ?? []
        : selectedScores;

      if (!isArrayEmpty(selectedAttributes)) {
        return this.onGetAttributesScoreDistribution(
          selectedAttributes,
          initialPreset
        ).then(() =>
          this.setState(
            {
              isLoading: false,
              selectedAttributes,
              selectedScores: updateSelectedScores(
                scores,
                this.props.scoreDistribution
              ),
              ...(initialPreset ? { period: initialPreset?.data?.period } : {}),
            },
            this.onGetAttributesUsersScores
          )
        );
      }

      if (!isArrayEmpty(scoreDistribution)) {
        clearAttributesScoreDistribution();
      }

      clearAttributesUsersScores();

      const emptyAttributesPeriod = initialPreset
        ? initialPreset?.data?.period
        : tempPeriod;

      this.setState(
        {
          isLoading: false,
          selectedAttributes,
          selectedScores: scores,
        },
        () =>
          this.checkValueInPresets(
            scores,
            emptyAttributesPeriod,
            getObjectToNumberArray(selectedAttributes)
          )
      );
    });
  };

  onGetAttributesScoreDistribution = (attributes, initialPreset = null) => {
    const { getAttributesScoreDistribution } = this.props;
    const { period, selectedSurveyType } = this.state;
    const tempPeriod = handlePeriod(initialPreset, period);

    const params = {
      attribute: getObjectToNumberArray(attributes),
      [PARAMS.SURVEY_TYPE]:
        initialPreset?.data?.survey_type ?? selectedSurveyType.value,
      [PARAMS.USED_AFTER]: tempPeriod.start,
      [PARAMS.USED_BEFORE]: tempPeriod.end,
    };

    return getAttributesScoreDistribution(params);
  };

  onGetAttributesUsersScores = () => {
    const { getAttributesUsersScores, clearAttributesUsersScores } = this.props;
    const { period, selectedScores, selectedAttributes, selectedSurveyType } =
      this.state;
    const hasSelectedScores = !isArrayEmpty(selectedScores);

    const params = {
      attribute: getObjectToNumberArray(selectedAttributes),
      score: selectedScores,
      [PARAMS.SURVEY_TYPE]: selectedSurveyType.value,
      [PARAMS.USED_AFTER]: period.start,
      [PARAMS.USED_BEFORE]: period.end,
    };

    if (hasSelectedScores) {
      return getAttributesUsersScores(params).then(() =>
        this.setState({ isScoreLoading: false }, () =>
          this.checkValueInPresets(selectedScores, period, params.attribute)
        )
      );
    }

    this.setState({ isScoreLoading: false }, () => {
      clearAttributesUsersScores();
      this.checkValueInPresets(selectedScores, period, params.attribute);
    });
  };

  handleAttributeSelect = attribute => {
    const { selectedAttributes, selectedScores } = this.state;
    const { translations } = this.props;
    const { MIN, MAX } = COMPETENCE_MAP_ATTRIBUTE_CONFIG;
    const isSelected = isItemInList(selectedAttributes, attribute);
    const attributesCount = selectedAttributes.length;
    let updatedAttributes = [];

    this.handleResetPreset();

    if (isSelected) {
      if (attributesCount > MIN) {
        updatedAttributes = selectedAttributes.filter(
          selectedAttribute => selectedAttribute.id !== attribute.id
        );
        this.setState(
          {
            selectedAttributes: updatedAttributes,
            defaultPlaceholder: true,
          },
          () =>
            this.onGetAttributesScoreDistribution(updatedAttributes).then(() =>
              this.setState(
                {
                  selectedScores: updateSelectedScores(
                    selectedScores,
                    this.props.scoreDistribution
                  ),
                },
                this.onGetAttributesUsersScores
              )
            )
        );
      } else {
        this.handleShowMinMaxNotification(
          `${translations.minAttribute} ${MIN}`
        );
      }
    } else if (attributesCount < MAX) {
      updatedAttributes = [...selectedAttributes, attribute];
      this.setState({ selectedAttributes: updatedAttributes }, () =>
        this.onGetAttributesScoreDistribution(updatedAttributes).then(() =>
          this.setState(
            {
              defaultPlaceholder: true,
              selectedScores: updateSelectedScores(
                selectedScores,
                this.props.scoreDistribution
              ),
            },
            this.onGetAttributesUsersScores
          )
        )
      );
    } else {
      this.handleShowMinMaxNotification(`${translations.maxAttribute} ${MAX}`);
    }
  };

  handlePeriodChange = newRange =>
    this.setState({ period: newRange, defaultPlaceholder: true }, () =>
      this.onGetMeasuredAttributes()
    );

  handleSurveyTypeChange = selectedSurveyType => {
    this.handleResetPreset();
    this.setState(
      {
        isLoading: true,
        selectedSurveyType,
        defaultPlaceholder: true,
      },
      () => this.onGetMeasuredAttributes()
    );
  };

  handleScoreSelect = scoreValue => {
    const { translations } = this.props;
    const { selectedScores } = this.state;
    const isSelected = selectedScores.includes(scoreValue);
    const selectedCount = selectedScores.length;

    if (!isSelected && selectedCount === MAX_SELECTED_SCORES) {
      return this.handleShowMinMaxNotification(
        `${translations.maxSelectedScores} ${MAX_SELECTED_SCORES}`
      );
    }

    this.setState(
      {
        isScoreLoading: true,
        defaultPlaceholder: true,
        selectedScores: isSelected
          ? selectedScores.filter(score => score !== scoreValue)
          : [...selectedScores, scoreValue],
      },
      this.onGetAttributesUsersScores
    );
  };

  handleShowMinMaxNotification = notificationMessage =>
    this.setState(
      { hasNotification: true, notificationMessage },
      asyncDebounce(
        () =>
          this.setState({ hasNotification: false, notificationMessage: '' }),
        NOTIFICATION_CLEAR_DELAY
      )
    );

  handlePlaceholder = name =>
    this.setState({
      defaultPlaceholder: false,
      presetPlaceholderName: name,
    });

  handleDeletePresetDialog = preset => {
    this.setState({ selectedPreset: preset, isDeletePresetDialogOpened: true });
  };

  handleDeletePreset = () => {
    const { deleteSelectedPreset, translations, presets } = this.props;
    const { selectedPreset } = this.state;

    deleteSelectedPreset(selectedPreset.id, COMPETENCE_MAP_PRESET_ID).then(
      () => {
        this.setState({
          isDeletePresetDialogOpened: false,
          ...(presets.active[COMPETENCE_MAP_PRESET_ID] === selectedPreset.id
            ? { selectedPreset: undefined, alreadySavedMessage: '' }
            : {}),
        });
        showSuccessMessage(translations.presets.successMessages.deletedPreset);
      }
    );
  };

  handleSavePreset = presetName => {
    const { translations, addNewPreset, editPresetActivity } = this.props;
    const { period, selectedAttributes, selectedScores, selectedSurveyType } =
      this.state;

    const data = {
      period,
      attributes: selectedAttributes,
      scores: selectedScores,
      survey_type: selectedSurveyType.value,
    };
    addNewPreset({
      name: presetName,
      location: COMPETENCE_MAP_PRESET_ID,
      data,
    }).then(newPreset => {
      editPresetActivity(newPreset.id, COMPETENCE_MAP_PRESET_ID, true);
      this.setState({
        selectedPreset: newPreset,
        alreadySavedMessage: translations.presets.used,
      });
      showSuccessMessage(translations.presets.successMessages.createdPreset);
    });
  };

  handleApplyPreset = preset => {
    const { editPresetActivity, translations } = this.props;
    const presetData = preset?.data;
    const surveyTypeOptions = prepareSurveyTypeFilterOptions(
      translations.surveyTypeFilterOptions
    );

    editPresetActivity(preset.id, COMPETENCE_MAP_PRESET_ID, true);
    this.setState(
      {
        period: presetData?.period,
        selectedAttributes: presetData?.attributes,
        selectedScores: presetData?.scores,
        selectedPreset: preset,
        selectedSurveyType:
          getSelectedSurveyType(surveyTypeOptions, presetData?.survey_type) ||
          INITIAL_SURVEY_TYPE_FILTER_VALUE,
      },
      () => this.onGetMeasuredAttributes(preset)
    );
  };

  preparePresetSummary = () => {
    const { translations } = this.props;
    const { period, selectedAttributes, selectedScores, selectedSurveyType } =
      this.state;

    return {
      hasSurveyType: true,
      hasRange: true,
      hasAttributes: true,
      hasScores: true,
      translations: {
        surveyType: translations.surveyType,
        range: translations.range,
        attributes: translations.attributes,
        scores: translations.selectedScore,
      },
      surveyTypes: selectedSurveyType?.label || '',
      range: {
        startDate: period[PARAMS.START],
        endDate: period[PARAMS.END],
      },
      attributes: selectedAttributes,
      scores: selectedScores,
    };
  };

  render() {
    const {
      classes,
      translations,
      attributes,
      scoreDistribution,
      usersScores,
      presets,
    } = this.props;
    const {
      isLoading,
      isScoreLoading,
      selectedAttributes,
      period,
      selectedScores,
      hasNotification,
      notificationMessage,
      alreadySavedMessage,
      isDeletePresetDialogOpened,
      selectedSurveyType,
    } = this.state;
    const activePresetId = getActivePresetId(presets, COMPETENCE_MAP_PRESET_ID);
    const filteredPresets = getFilteredPresets(
      presets,
      COMPETENCE_MAP_PRESET_ID
    );
    const presetName = getActivePresetName(filteredPresets, activePresetId);
    const handledPresetName = handlePresetName(
      filteredPresets,
      activePresetId,
      presetName,
      this.state,
      translations.presets.startSearch
    );
    const placeholderName = getPlaceholderName(
      handledPresetName,
      presetName,
      translations.presets.startSearch
    );
    const hasAttributes = !isArrayEmpty(attributes);
    const hasScore = !isArrayEmpty(scoreDistribution);
    const hasSelectedScores = !isArrayEmpty(selectedScores);
    const hasUsersScores = !isArrayEmpty(usersScores);
    const matchedUsers = prepareMatchedUsers(usersScores, selectedAttributes);
    const hasMatchedUsers = !isArrayEmpty(matchedUsers);
    const surveyTypeOptions = prepareSurveyTypeFilterOptions(
      translations.surveyTypeFilterOptions
    );

    return (
      <div>
        <div className={classes.description}>
          <Typography variant="body2">{translations.description}</Typography>
        </div>
        <div className={classes.filter}>
          <SelectField
            label={translations.surveyType}
            value={selectedSurveyType}
            options={surveyTypeOptions}
            onChange={this.handleSurveyTypeChange}
            shouldReturnOption
            isSearchDisabled
          />
          <CustomDateRange
            label={translations.range}
            initialRange={{
              startDate: period[PARAMS.START],
              endDate: period[PARAMS.END],
            }}
            startAtKey={PARAMS.START}
            endAtKey={PARAMS.END}
            onChange={this.handlePeriodChange}
          />
          <PresetDropdown
            translations={translations.presets}
            options={filteredPresets}
            activePreset={activePresetId}
            handleApply={this.handleApplyPreset}
            handleDelete={this.handleDeletePresetDialog}
            handleSavePreset={this.handleSavePreset}
            disabledPreset={handleHasMaxPresets(
              filteredPresets,
              translations.presets
            )}
            alreadySavedMessage={alreadySavedMessage}
            presetPlaceholderName={placeholderName}
            presetSummary={this.preparePresetSummary()}
            handlePlaceholder={this.handlePlaceholder}
          />
        </div>
        {!isLoading && (
          <>
            <div className={classes.attributesWrapper}>
              <Typography className={classes.attributesLabel} variant="h5">
                {translations.attributes}
              </Typography>
              {hasAttributes ? (
                <AttributesList
                  attributes={attributes}
                  selectedAttributes={selectedAttributes}
                  onSelect={this.handleAttributeSelect}
                  isHorizontalList
                  isScrollable
                />
              ) : (
                <NotificationCard content={translations.noRecords} />
              )}
              {hasNotification && (
                <Typography className={classes.notification} variant="caption">
                  {notificationMessage}
                </Typography>
              )}
            </div>
            {hasAttributes && hasScore && (
              <CompetenceMapChart
                className={classes.chart}
                yAxisLabel={translations.yAxisLabel}
                data={prepareChartData(scoreDistribution)}
                selectedScores={selectedScores}
                onScoreSelect={this.handleScoreSelect}
              />
            )}
            <Typography className={classes.scoresTitle} variant="h4">
              {`${translations.selectedScore} ${
                hasSelectedScores ? `(${selectedScores.join(', ')})` : ''
              }`}
            </Typography>
            {hasSelectedScores && hasMatchedUsers && (
              <MatchedUsersList
                matchedUsers={matchedUsers}
                isUserProfileAccessible={this.canAccessUserProfileCheck}
              />
            )}
            <NotificationCard
              content={translations.noScores}
              shouldFade={
                !isScoreLoading && !hasSelectedScores && !hasUsersScores
              }
              shouldFadeOutInstant
            />
            <NotificationCard
              content={translations.noMatchedUsers}
              shouldFade={
                hasSelectedScores && !isScoreLoading && !hasMatchedUsers
              }
              shouldFadeOutInstant
            />
          </>
        )}
        <AlertDialog
          translations={translations.presets.presetDeleteDialog}
          isOpened={isDeletePresetDialogOpened}
          onClose={() => this.setState({ isDeletePresetDialogOpened: false })}
          onConfirm={this.handleDeletePreset}
          isWarning
        />
      </div>
    );
  }
}

CompetenceMapPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  attributes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  scoreDistribution: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  usersScores: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getMeasuredAttributes: PropTypes.func.isRequired,
  getAttributesScoreDistribution: PropTypes.func.isRequired,
  getAttributesUsersScores: PropTypes.func.isRequired,
  clearMeasuredAttributes: PropTypes.func.isRequired,
  clearAttributesScoreDistribution: PropTypes.func.isRequired,
  clearAttributesUsersScores: PropTypes.func.isRequired,
};

export default withStyles(styles)(CompetenceMapPage);
